import type {
  BooleanFlag,
  GeneralID,
  LinkTarget,
  MediaType,
  ModalSize,
  ModalSizeType,
  OpenStyle,
  SceneType,
} from './baseTypes';
import type { ComponentLayer } from './componentLayerTypes';
import type { SceneEffect } from './effectTypes';
import type {
  CFSceneSwitchType,
  CustomFeature,
  FeatureType,
} from './featureTypes';
import type { HotspotGroups } from './groupsTypes';
import type { Hotspot } from './hotspotTypes';
import type { ST } from './sandTableTypes';
import type { ViewData } from './viewTypes';

export type CategoryConfig = Partial<{
  enabledButton: BooleanFlag; // 是否显示场景选择按钮,  默认1
  buttonMediaId: number;
  buttonMediaUrl: string;
  buttonTitle: string; // 按钮标题
  enabledColumn: BooleanFlag; // 是否显示场景选择栏, 默认1
  columnStyle: 0; // [[UNUSED]] 选择栏样式, 默认0
  switchCategoryShowFirstScene: BooleanFlag; // # 分组是否自动切换, 默认0
  isDefaultShowColumn: BooleanFlag; // 是否默认显示场景选择栏, 默认1
  initScene: GeneralID; // 场景ID，默认没有该属性
  effect: CFSceneSwitchType; // 场景切换效果 v5.2.7
}>;

export enum LogoType {
  disabled = 0,
  system = 1,
  custom = 2,
}

export enum LogoAction {
  none = 0,
  link = 1,
}

export type Logo = Partial<{
  type: LogoType; // logo类型, 默认1
  iconMediaId: number;
  iconMediaUrl: string;
  action: LogoAction; // 点击事件, 默认0
  scale: number;
  linkUrl: string;
  openMethod: LinkTarget; // 打开链接方式, 默认1
  turnOffBgMusicWhenLayerOpen: BooleanFlag; // 弹层打开时关闭背景音乐, 默认0
  turnOffBgVoiceWhenLayerOpen: BooleanFlag; // 弹层打开时关闭背景讲解, 默认0
  modalSizeType?: ModalSizeType;
  size: ModalSize;
  openStyle: OpenStyle;
}>;

export type GlobalFlags = Partial<{
  /** 是否显示作者昵称, 默认1 */
  showAuthorName: BooleanFlag;
  /** 是否显示漫游访问量, 默认1 */
  showTourPV: BooleanFlag;
  /** 是否显示漫游简介, 默认1 */
  showTourRemark: BooleanFlag;
  /** 是否显示场景名字, 默认1 */
  showSceneName: BooleanFlag;
  /** 是否显示全屏按钮, 默认1 */
  showFullscreenBtn: BooleanFlag;
  /** 是否显示VR按钮, 默认1 */
  showVRBtn: BooleanFlag;
  /** 是否显示视角切换按钮, 默认1 */
  showViewSwitchBtn: BooleanFlag;
  /** 是否显示陀螺仪按钮, 默认1 */
  showGyroBtn: BooleanFlag;
  /** 是否默认打开陀螺仪, 默认0 */
  defaultEnabledGyro: BooleanFlag;
  /** 是否显示分享功能, 默认1 */
  showShareBtn: BooleanFlag;
  /** 是否显示点赞功能, 默认1 */
  showLikeBtn: BooleanFlag;
  /** 是否显示说一说内容, 默认1 */
  showComment: BooleanFlag;
  /** 拖拽方式，0:反向 1:正向 默认1 */
  dragMode: BooleanFlag;
  /** 是否显示自动巡游图标，0:不显示 1:显示 默认0, v5.3.8废弃 */
  showAutoRotateBtn: BooleanFlag;
  /** 缩放回弹效果, 0:不开启 1:开启 默认1, v5.3.6 */
  enableBouncing: BooleanFlag;
  /** 是否作者审核说一说, 0:不开启 1:开启 默认0 v5.3.8 */
  checkCommentByAuthor: BooleanFlag;
  /** 是否显示说一说功能, 0:不显示 1:显示 默认1 v5.3.8 */
  showCommentBtnV2: BooleanFlag;
  /** 是否显示足迹, 0:不显示 1:显示 默认1 v5.5.1 */
  showMap: BooleanFlag;
  /** 是否显示热点开关 0:不显示 1:显示 默认0 v5.8.7 */
  showHotspotBtn: BooleanFlag;
  /** 是否强制横屏 0:否 1:是 默认0 v5.8.10 */
  forceLandscape: BooleanFlag;
  /** 是否默认进入场景对比模式 默认0 v5.8.12 */
  compareMode: BooleanFlag;
}>;

export enum EnterCoverType {
  image = 0,
  video = 1,
}
export enum EnterCoverPosition {
  center = 0,
  fullscreen = 1,
}
export enum EnterCoverEnter {
  auto = 0,
  click = 1,
}

export type EnterCover = Partial<{
  type: EnterCoverType; // 默认0
  bgColor: string;
  bgImageId: number;
  bgImageUrl: string;
  pcCoverId: number;
  pcCoverUrl: string;
  pcCoverVideoName: string;
  pcCoverVideoDuration: number;
  pcCoverVideoThumb: string;
  pcShowPosition: EnterCoverPosition; // 默认0
  mobileCoverId: number;
  mobileCoverUrl: string;
  mobileShowPosition: EnterCoverPosition; // 默认0
  mobileCoverVideoName: string;
  mobileCoverVideoDuration: number;
  mobileCoverVideoThumb: string;
  enterMethod: EnterCoverEnter; // 默认 0
  enterDuration: number; // 单位秒 默认5
  showCountDown: BooleanFlag; // 是否显示倒计时,默认0
  autoPlay: BooleanFlag; // 是否自动播放, 0不自动, 1自动 默认1
  loop: BooleanFlag; // 是否循环, 0不自动, 1自动 默认0
}>;

export type StartAnime = Partial<{
  pano: 0 | 1 | 2 | 3 | 4 | 5; // 0-关闭, 1-小行星, 2-水平巡游, 3-水晶球, 4-小行星巡游, 5-小行星缩放
  video: 0 | 1 | 2 | 3; // 0-关闭, 1-小行星, 2-水平巡游, 3-水晶球
  matrix: 0 | 1; // 0-关闭, 1-缩放开场
}>;

export type AutoRotate = Partial<{
  enabled: BooleanFlag; // 是否开启, 默认1
  duration: number; // 单位秒 默认180
  autoEnterNext: BooleanFlag; // 是否自动进入下一个场景, 默认1
  hiddenThumbSceneNoEnter: BooleanFlag; // 设置隐藏缩略图的场景不跳转,  默认1
}>;

export type TourTip = Partial<{
  enabled: BooleanFlag; // 是否显示, 0不显示 1显示 默认1
  pcMediaId: number;
  pcMediaUrl: string;
  mobileMediaId: number;
  mobileMediaUrl: string;
  duration: number; // 显示时长, 单位秒 默认3
}>;

export enum EditConfigLanguageType {
  system = 0,
  cn = 1,
  en = 2,
}

export type EditConfigLanguage = Partial<{
  type: EditConfigLanguageType; // 语音类型设置, 默认1
  showButton: BooleanFlag; // 是否显示切换按钮,, 默认0
  relationUrl: string; // 关联的作品地址
}>;

export type EditConfigUI = Partial<{
  /** 字体色, 默认'199,199,199,1' */
  fontColor: string;
  /** 字体选中色，默认'255,255,255,1' */
  fontAvtiveColor: string;
  /** 背景色，默认'0,0,0,1' */
  bgColor: string;
  /** 按钮选中颜色，默认'40,110,250,1' */
  btnActiveColor: string;
  /** 画布背景色，默认'#000000', v5.8.1新增 */
  canvasBgColor: string;
  /** 弹出面板背景色, 默认 '255,255,255,1', v5.8.7 */
  panelBgColor: string;
}>;

export type EditConfigCopyright = Partial<{
  title: string;
  content: string;
}>;

export type EditConfigSandTable = Partial<{
  showWhenLoad: BooleanFlag; // # 显示开场沙盘, 0:不显示 1:显示 默认1
  list: string[];
  defaultId: string; // # 默认沙盘id
}>;

export enum EditConfigLoadingType {
  system = 0, // 系统默认
  text = 1, // 文字
  custom = 2, // 自定义
}

export type EditConfigLoading = Partial<{
  type: EditConfigLoadingType;
  mediaId: number;
  mediaUrl: string;
  bgColor: string; // '0,0,0,1
  itemColor: string; // 图标或文字颜色
}>;

export type EditConfigSearch = Partial<{
  /** 搜索是否启用, 0:不启用 1:启用 默认0 */
  enabled: BooleanFlag;
  /** 是否能搜场景, 0:不能 1:能 */
  scenes: BooleanFlag;
  /** 能搜索的热点类型, 如[1,2,8] */
  hotspotTypes: FeatureType[];
  /** 是否自动打开热点, 0不打开， 1打开，默认1 v5.8.7 */
  autoOpen: BooleanFlag;
  /** 仅搜索但前场景热点，0关闭，1打开，默认0 v5.8.7 */
  searchInCurrentScene: BooleanFlag;
}>;

type CustomIcon = Partial<{
  type: MediaType;
  mediaId: number;
  mediaUrl: string;
}>;

export type EditConfigCustomIcons = Partial<{
  shareBtn: CustomIcon;
  likeBtn: CustomIcon;
  likeBtnActive: CustomIcon;
  commentBtn: CustomIcon;
  remarkBtn: CustomIcon;
}>;

export type EditConfig = Partial<{
  language: EditConfigLanguage;
  ui: EditConfigUI;
  copyright: EditConfigCopyright;
  sandBox: EditConfigSandTable;
  loading: EditConfigLoading;
  firstSceneButton: SceneConfigFirstSceneButton;
  search: EditConfigSearch;
  customIcons: EditConfigCustomIcons;
  extendData: ExtendData;
  vipView: EditConfigVipView;
  /** 创作者昵称开启后支持自定义设置, v5.8.12 */
  author: EditConfigAuthor;
}>;

export type EditConfigAuthor = Partial<{
  /** `0`使用账户名称, `1`使用归属人姓名 `2`自定义, 默认`0` */
  mode: 0 | 1 | 2;
  /**  自定义昵称 */
  customNickname: string;
}>;

export type EditConfigVipView = Partial<{
  enabled: BooleanFlag;
  // 场景ID，逗号分割
  sceneList: string;
  // 金额
  payment: number;
}>;
export interface ExtendData {
  partnerId: string;
}
export interface CategoryData {
  id: number;
  name: string;
  children?: CategoryData[];
}

export enum CustomButtonType {
  group = 0,
  button = 1,
}

export type CustomButton = {
  type: CustomButtonType;
  iconMediaId: number;
  iconMediaUrl: string;
  groupTitle: string;
  children: CustomFeature[];
};

export type PanoMultires = {
  level?: number;
  tiledimage?: any[];
};

export interface TranscodeTrackItem {
  Definition: string;
  FileUrl: string;
  Status: string;
}

export interface TranscodeTrack {
  super?: TranscodeTrackItem;
  high?: TranscodeTrackItem;
  standard?: TranscodeTrackItem;
}

export interface MatrixFov {
  hfov?: string;
  vfov?: string;
  voffset?: string;
}

export interface Audio {
  /** v5.2.3新增，以前皆为素材库类型 1 */
  type?: MediaType;
  /** 0 表示系统音乐 */
  mediaId?: number;
  mediaUrl?: string;
  mediaName?: string;
  /** 循环, 默认1 */
  isLoop?: BooleanFlag;
  /** 音量 0-1 */
  volume?: number;
  /** 自动播放 默认1 */
  autoPlay?: BooleanFlag;
}

export enum VoiceLoopType {
  none = 0,
  loop = 1,
  switch = 2,
}

export interface Voice {
  /** v5.3.1 转换为 autoRotate 的 endOperation */
  type?: MediaType;
  /** 0 表示系统音乐 */
  mediaId?: number;
  mediaUrl?: string;
  mediaName?: string;
  isLoop?: VoiceLoopType;
  /** 音量 0-1 */
  volume?: number;
  /** 自动播放 默认1 */
  autoPlay?: BooleanFlag;
  /** 结束后停顿时长 默认0 */
  playEndDelay?: number;
  /** 讲解一遍结束后不再讲解, 默认0  */
  stopAfterPlayOnce?: BooleanFlag;
}

export type MaskSetting = Partial<{
  enabled: BooleanFlag;
  mediaId: number; // 0表示系统图标
  mediaUrl: string;
  stick: BooleanFlag; // 是否贴合全景，跟随画面同步缩放, 1开启, 0关闭, 默认1
  scale: number; // 0-1, 默认 0.5
  rotate: number; // 0-360
}>;

export interface Mask {
  top?: MaskSetting;
  bottom?: MaskSetting;
}

export enum AutoRotateType {
  none = 0,
  right = 1,
  left = 2,
  custom = 3,
}

export enum AutoRotateAction {
  // 巡游交互 0循环 1巡游一圈自动跳转 2巡游一定时间自动跳转 默认1
  loop = 0,
  byRound = 1,
  byTime = 2,
}

export enum AutoRotateEndOperation {
  // 巡游结束操作, 0循环, 1讲解结束跳转, 2导览结束跳转, 3结束后停止
  loop = 0,
  byVoice = 1,
  byTour = 2,
  stop = 3,
}

export type AutoRotateItem = {
  id: string;
  // 开始移动时间
  startAt: number;
  // 移动时长, 秒
  duration: number;
  // 目标水平视角
  hlookat: number;
  // 目标垂直视角
  vlookat: number;
  // 目标视角fov
  fov: number;
  // 移动结束后操作, 0无操作
  action: 0;
};

export type SceneConfigAutoRotate = Partial<{
  // 巡游方式 默认1
  type: AutoRotateType;
  // 巡游时间, 单位秒 默认180
  duration: number;
  // 停留时长, 单位秒 默认180
  stayTime: number;
  // 保持视角 0关闭 1开启 默认1
  holdView: BooleanFlag;
  // 巡游交互, v5.3.1 转为 endOperation
  rotateAction: AutoRotateAction;
  // 巡游结束操作, 默认2，v5.3.1 新增
  endOperation: AutoRotateEndOperation;
  // 字幕 v5.3.1
  subtitle: string;
  // 字幕文件名 v5.3.1
  subtitleFileName: string;
  // 自定义巡游动作列表
  tourData: AutoRotateItem[];
  // 跳转目标场景, 为空时跳转下一个, 默认 "", v5.3.6
  targetSceneId: GeneralID;
  // 跳转目标场景时的切换效果, v5.3.6
  animation?: CFSceneSwitchType;
}>;

export type SceneConfigCompass = {
  /** 1打开, 0关闭, 默认0 */
  enabled: BooleanFlag;
  /** 0系统, 1自定义, 目前只有0 */
  type: MediaType;
  /** 素材id */
  mediaId: number;
  /** 图片路径 */
  mediaUrl: string;
  /** 显示位置, 默认/存量数据 `topleft`, v5.8.12 */
  position?: 'topleft' | 'bottom';
};

export type SceneConfigFirstSceneButton = Partial<{
  enabled: BooleanFlag; // 1打开,0关闭,默认0
  label: string; // 按钮标题, 默认 ‘返回’
  fontColor: string; // 文字颜色, 默认 '255,255,255,1'
  bgColor: string; // 背景颜色, 默认 '0,0,0,1'
}>;

export type SceneConfigLayers = Partial<{
  list: {
    id: string;
    fovMin: number;
    fovMax: number;
    hotspotIds: string[];
  }[];
}>;

export type SceneConfig = Partial<{
  autoRotate: SceneConfigAutoRotate;
  compass: SceneConfigCompass;
  layers: SceneConfigLayers;
  components: ComponentLayer[];
  /** 0:原画(默认) 1:高清 2:4K 3:2K 4:极速 */
  defaultResolution: 0 | 1 | 2 | 3 | 4;
  /** 场景新增第三方ID, v5.8.7 */
  partnerId: string;
}>;

export enum VideoLoopType {
  switch = 1,
  loop = 2,
  none = 3,
}

export enum VideoPosType {
  bottomRight = 1,
  bottomLeft = 2,
  bottomCenter = 3,
}

export type Video = Partial<{
  mediaId: number;
  mediaUrl: string;
  mediaName: string;
  mediaDuration: number;
  /** 视频封面 */
  videoThumbUrl: string;
  /** 默认的话为空 */
  partnerKey: 'v';
  /** 抠图色值 */
  maskColor: string;
  /** 抠图相似度 */
  maskSimilarity: number;
  /** 字幕内容 */
  subtitle: string;
  /** 位置 1:右下 2:左下 3:底部居中 */
  pcPosition: VideoPosType;
  /** 位置 1:右下 2:左下 3:底部居中 */
  mobilePosition: VideoPosType;
  /** v5.3.1 转换为 autoRotate 的 endOperation */
  playSetting: VideoLoopType;
  /** 播放结束后的停留时长 */
  playEndDelay: number;
  /** 缩放比例 */
  scale: number;
  /** 讲解一遍结束后不再讲解, 默认0  */
  stopAfterPlayOnce?: BooleanFlag;
}>;

export enum SubtitleRollingMode {
  static = 0,
  left = 1,
}

export enum SubtitleClickAction {
  none = 0,
  link = 1,
}

export type Subtitle = {
  text: string; // 字幕内容
  fontSize: number; // 文字大小 14
  fontColor: string; // 文字颜色, '255,255,255,1'
  bgColor: string; // 背景颜色, '0,0,0,1'
  spacing: number; // 字间距 0
  rollingMode: SubtitleRollingMode; // 0静止显示, 1向左滚动
  rollingSpeed: number; // 0.5 - 2.0
  pauseDuration: number; // 停留时长, 单位秒 10
  clickAction: SubtitleClickAction; // 0无操作, 1打开超链接
  clickActionLink: string; // 超链接
  linkTarget: LinkTarget;
  turnOffBgMusicOnPlay: BooleanFlag; // 1
  modalSizeType?: ModalSizeType;
  size?: ModalSize;
};

export enum TagLineAlignMode {
  middle = 0,
  above = 1,
  bellow = 2,
}
export enum TagLinePointMode {
  none = 0,
  circle = 1,
  square = 2,
  arrow = 3,
}
export interface TagLine {
  id: string;
  startAth: number; // 起始 ath
  startAtv: number; // 起始 atv
  endAth: number; // 结束 ath
  endAtv: number; // 结束 atv
  title: string; // 标尺文字
  titleSetting: {
    alignMode: TagLineAlignMode; // 文字位置 0中间, 1上方, 2下方
    distorted: BooleanFlag; // 是否跟随画面缩放, 0/1, 默认1
    enableRotate: BooleanFlag; // 是否开启自定义文字角度
    rotate: number; // 文字角度 0-360
    fontSize: number; // 字号, 默认14
    fontColor: string; // 颜色  '0,0,0,1'
    bgColor: string; // 背景色 '0,0,0,1' RGBA
  };
  style: {
    lineWidth: number; // 线段粗细
    lineColor: string; // 线段颜色
    pointMode: TagLinePointMode; // 端点类型 0无端点, 1圆点, 2端线, 3箭头
    pointColor: string; // 端点颜色
  };
}

export interface SceneCompareItem {
  /** 项id */
  id: string;
  /** 素材类型, 对应场景type */
  type: SceneType;
  /** 对比素材ID */
  materialId: number;
  /** 标题 */
  title: string;
  /** 初始视角偏移角度 */
  viewOffset: { hLookAt: number; vLookAt: number; fov: number };
}

export interface SceneCompareConfig {
  /** 原始场景对比标题 */
  originTitle: string;
  /** 是否开启对比模式切换0/1，默认1 */
  showModeOption: 0 | 1;
  /** 是否显示场景列表0/1, 默认0 */
  showScenesList: 0 | 1;
  /** 对比场景 */
  scenes: SceneCompareItem[];
}

export type SceneData = {
  id: GeneralID;
  tourId: number;
  type: SceneType;
  materialId: number;
  name: string;
  thumbUrl: string;
  createDate: number;
  updateDate: number;
  categoryId: number;
  showThumb: BooleanFlag;
  multires: PanoMultires;
  cdnDomain: number;
  /** 图片路径 */
  path: string;
  view: ViewData;
  /** 场景关联的漫游作品的TID */
  tid: string;
  transcodeTrack: TranscodeTrack;
  matrixFov: MatrixFov;
  sort: number;
  mask: Mask;
  hotspot?: Hotspot[];
  // "subtitle": ${subtitle},
  sound: Audio;
  voice: Voice;
  video: Video[];
  // "keypoint": ${keypoint},
  config: SceneConfig;
  /** 相对正北偏移角度 */
  north: number;
  sandBox: {
    /** 沙盘列表 */
    list?: string[];
    defaultId?: string;
  };
  effect?: SceneEffect[];
  subtitle?: Subtitle[];
  groups?: HotspotGroups;
  tagLine?: TagLine[];
  /** 全景视频原始地址 (v5.7.5新增) */
  originTrack?: string;
  /** 对比模块 v5.8.7 */
  compareConfig?: SceneCompareConfig;
};

export enum TemplateId {
  vip = 1,
  free = 2,
  simple = 3,
  realEstateA = 4,
  metaverseA = 6, // 5为vr店模版
  realEstateB = 7,
  legacy = 8,
  project = 9, // v5.8.7, 工程记录
}

export enum PrivilegeType {
  private = 0,
  public = 1,
  password = 2,
  team = 3,
}

export type TourData = Readonly<{
  id: number;
  tid: string;
  name: string;
  remark: string;
  thumbUrl: string;
  keywords: string;
  channelId?: number;
  subChannel?: string;
  createDate: number;
  updateDate: number;
  templateId: TemplateId;
  // albumId: number;
  expired: BooleanFlag;
  expireDate: number;
  operatorId: number;
  operatorName: string;
  category: CategoryData[];
  categoryConfig: CategoryConfig;
  scenes: SceneData[];
  logo: Logo;
  flag: GlobalFlags;
  enterCover: EnterCover;
  startAnime: StartAnime;
  autoRotate: AutoRotate;
  tourTip: TourTip;
  config: EditConfig;
  customButton: CustomButton[];
  sandBox: ST[];
  privilege: PrivilegeType;
  password: string;
  shareConfig: string;
}>;
